import React from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { H1, P } from '../../../../components/Typography';
import Modal from '../../../../components/Modal';
import { Button, ButtonGroup } from '../../../../components/Buttons';
import { distances } from '../../../../styles/constants';
import { SwishModalState, State as SwishState } from '../reducer';
import { LoadingOverlay } from '../../../../components/Loading';
import { AccountIdsProps, withAccountIds } from '../../../../auth/accessToken/withAccountIds';
import { Formik } from 'formik';
import { SwishConnectionSignup } from '../../../../types/management-auth/generated';

export interface SwishModalProps extends WithTranslation, AccountIdsProps {
    initiateSwishConnection: (accountId: string, data: SwishConnectionSignup) => void;
    closeSwishModal: () => void;
    openSwishForm: () => void;
    swish: SwishState;
}

interface FormikValueProps {
    [key: string]: any;
}

const SwishModal = (
    {
        t,
        swish,
        closeSwishModal,
        initiateSwishConnection,
        prodAccountId,
    }: SwishModalProps
) => {
    if (!prodAccountId) {
        return null;
    }

    if (swish.swishModalState === SwishModalState.Closed) {
        return null;
    }

    const account = swish.account;

    const validateValues = (values: FormikValueProps) => {
        const errors: { [key: string]: string } = {};
        if (!values) {
            // this should not happen but...
            return { error: 'account not loaded yet...' };
        }
        return errors;
    };

    if (!account) {
        return <LoadingOverlay />;
    }

    const initialFormValues: FormikValueProps = {};
    const initialValidation = validateValues(initialFormValues);

    return (
        <Modal fixedWidth>
            {[SwishModalState.ApplicationFormOpened, SwishModalState.InitiateApplicationLoading].includes(
                swish.swishModalState
            ) && (
                <>
                    <H1>{t('settings.payment_connections.swish_modal.title_intro')}</H1>
                    <Formik
                        enableReinitialize
                        initialValues={initialFormValues}
                        initialErrors={initialValidation}
                        validate={validateValues}
                        onSubmit={_ => {
                            if (
                                !account ||
                          !account.company ||
                          !account.company.organization_number ||
                          !account.company.website ||
                          !prodAccountId
                            ) {
                            // How could we ever get here?
                                return;
                            }
                            initiateSwishConnection(prodAccountId, {
                                business_name: account.company.business_name,
                                legal_name: account.company.business_name,
                                organization_number: account.company.organization_number || '',
                                website: account.company.website || '',
                                payment_options: [
                                    {
                                        currencies: ['SEK'],
                                        type: 'swish.swish',
                                    },
                                ],
                            });
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            touched,
                            handleSubmit,
                            isValid,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <SpacedWrapper>
                                        <P>
                                            <Trans i18nKey="settings.payment_connections.swish_modal.intro" ><strong /><code/></Trans>
                                        </P>
                                    </SpacedWrapper>
                                    <ButtonGroup>
                                        <Button type="submit" disabled={!isValid}>
                                            {t(
                                                'settings.payment_connections.swish_modal.sign_up_with_swish_call_to_action'
                                            )}
                                        </Button>
                                        <Button className="alt" onClick={closeSwishModal}>
                                            {t('settings.payment_connections.swish_modal.cancel')}
                                        </Button>
                                    </ButtonGroup>
                                    {swish.swishModalState === SwishModalState.InitiateApplicationLoading && (
                                        <LoadingOverlay />
                                    )}
                                </form>
                            );
                        }}
                    </Formik>
                </>
            )}
            {swish.swishModalState === SwishModalState.ApplicationReceived && (
                <>
                    <H1>{t('settings.payment_connections.swish_modal.title_last_step')}</H1>

                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.swish_modal.last_step" />
                        </P>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <Button onClick={closeSwishModal}>{t('settings.payment_connections.swish_modal.close')}</Button>
                    </ButtonGroup>
                </>
            )}
        </Modal>
    );
};

export default withAccountIds(withTranslation()(SwishModal));

const SpacedWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.normal};
`;
